import React, { createContext, useContext } from "react";
import { Constants, Store } from "utils";

interface ProvidedValueType {
  user: any;
  setUser: (user: any) => void;
  t: any;
  locale: string;
  locales: string[];
  setLocale: (language: string) => void;
}

interface Props {
  initLocale: string;
  children?: React.ReactNode;
}

const getStoredLocale = (): string => {
  const stored = Store.appStorage.get();
  if (stored.locale && Constants.LOCALE_STRINGS[stored.locale.toUpperCase()]) {
    return stored.locale.toUpperCase();
  }
  return "EN";
};

const initialLocale = getStoredLocale();

const initialState = {
  locale: initialLocale,
  translations: Constants.LOCALE_STRINGS[initialLocale] || Constants.LOCALE_STRINGS.EN,
  locales: Object.keys(Constants.REGIONS) as string[],
  user: Store.appStorage.get().user || null,
};

export const AppContext = createContext<ProvidedValueType>({
  user: initialState.user,
  setUser: () => {},
  t: initialState.translations,
  locale: initialState.locale,
  locales: initialState.locales,
  setLocale: () => {},
});

export const AppProvider = React.memo<Props>(
  ({ initLocale, children }: Props) => {
    const [user, setUser] = React.useState<any>(initialState.user);
    const [locale, setLocale] = React.useState<string>(() => {
      const stored = Store.appStorage.get();
      const candidate = (stored.locale || initLocale || "EN").toUpperCase();
      return Constants.LOCALE_STRINGS[candidate] ? candidate : "EN";
    });

    const setUserCallback = React.useCallback((newUser: any | null) => {
      setUser((currentUser: any) => {
        if (newUser === null) {
          Store.appStorage.set({ user: null });
          return null;
        }

        const merged = { ...currentUser, ...newUser };
        Store.appStorage.set({ user: merged });
        return merged;
      });
    }, []);

    const setLocaleCallback = React.useCallback((newLocale: string) => {
      const upper = (newLocale || "EN").toUpperCase();
      const valid = Constants.LOCALE_STRINGS[upper] ? upper : "EN";
      setLocale(valid);
      Store.appStorage.set({ locale: valid });
    }, []);

    const translations = React.useMemo(() => {
      const upper = (locale || "EN").toUpperCase();
      return Constants.LOCALE_STRINGS[upper] || Constants.LOCALE_STRINGS.EN;
    }, [locale]);

    const value = React.useMemo<ProvidedValueType>(
      () => ({
        locale,
        t: translations,
        setLocale: setLocaleCallback,
        locales: initialState.locales,
        user,
        setUser: setUserCallback,
      }),
      [locale, translations, user, setUserCallback, setLocaleCallback],
    );

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
  },
);

export const useApp = () => useContext(AppContext);
